import { nodeCrypto } from "./node-crypto-lazy.ts";

import {
  SourceStructureSelectionV1Schema,
  resolveOpenPencilSourceStructureSelection,
  type SourceStructureSelectionV1,
} from "./admission-v2.ts";
import {
  getPreflightOpenPencilOntologyFactsV2,
  openVerifiedOpenPencilDesignTokenReadbackV2,
  preflightOpenPencilDesignTokenBoundaryV2,
  type OpenPencilOntologyBoundaryInputV2,
  type OpenPencilOntologySourceRefsV2,
} from "./ontology-candidate-boundary-v2.ts";
import {
  type OpaqueId,
  type Sha256,
  type StructuralFact,
} from "./fact-shards.ts";

const NAMESPACE = "cogo.openpencil.structural.v1";
const OPAQUE_PATTERN = /^hmac-sha256:[a-f0-9]{64}$/;

export type DesignTokenType =
  | "color"
  | "number"
  | "string"
  | "boolean"
  | "unknown";

export type CanonicalDesignTokenRole =
  | "color"
  | "typography"
  | "spacing"
  | "sizing"
  | "radius"
  | "border"
  | "elevation"
  | "opacity"
  | "unmapped";

export interface DesignTokenOntologyCandidateInputV2 extends OpenPencilOntologyBoundaryInputV2 {
  allowedCollectionOpaqueIds: OpaqueId[];
}

export interface DesignTokenOntologyCandidateV2 {
  schemaVersion: "cogo.design-token-ontology-candidate.v2";
  collections: Array<{
    sourceSemanticRef: OpaqueId;
    modeSemanticRefs: OpaqueId[];
    tokenCount: number;
  }>;
  tokenRoles: Array<{
    sourceSemanticRef: OpaqueId;
    tokenType: DesignTokenType;
    canonicalRole: CanonicalDesignTokenRole;
    semanticSignature: Sha256;
  }>;
  gaps: Array<{
    code:
      | "unmapped-token-role"
      | "ambiguous-token-role"
      | "raw-value-requested"
      | "mode-inconsistency";
    sourceSemanticRef: OpaqueId;
  }>;
  sourceRefs: OpenPencilOntologySourceRefsV2;
  authority: {
    candidateOnly: true;
    generationEligible: false;
  };
}

type JsonRecord = Record<string, unknown>;
type Primitive = string | number | boolean | null;

type RawMode = {
  raw: JsonRecord;
  name: string;
  nameId: OpaqueId;
  signatureId: OpaqueId;
};

type RawToken = {
  raw: JsonRecord;
  name: string;
  nameId: OpaqueId;
  tokenType: DesignTokenType;
  tokenTypeCode: `token-type:${DesignTokenType}`;
  baseObservationId: OpaqueId;
  modePairs: Array<{
    mode: RawMode;
    observationId: OpaqueId;
  }>;
  signatureId: OpaqueId;
};

type RawCollection = {
  raw: JsonRecord;
  name: string;
  nameId: OpaqueId;
  modes: RawMode[];
  tokens: RawToken[];
  semanticGroupOpaqueId?: OpaqueId;
  subjectOpaqueId?: OpaqueId;
};

function isRecord(value: unknown): value is JsonRecord {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function canonicalize(value: unknown, seen = new WeakSet<object>()): unknown {
  if (
    value === null ||
    typeof value === "string" ||
    typeof value === "boolean"
  ) {
    return value;
  }
  if (typeof value === "number") {
    if (!Number.isFinite(value)) {
      throw new Error("DESIGN_TOKEN_ONTOLOGY_CANDIDATE_NON_FINITE_NUMBER");
    }
    return value;
  }
  if (Array.isArray(value)) {
    return value.map((entry) => canonicalize(entry, seen));
  }
  if (!isRecord(value)) {
    throw new Error("DESIGN_TOKEN_ONTOLOGY_CANDIDATE_CANONICAL_JSON_INVALID");
  }
  if (seen.has(value)) {
    throw new Error("DESIGN_TOKEN_ONTOLOGY_CANDIDATE_CANONICAL_JSON_CYCLE");
  }
  seen.add(value);
  const result: JsonRecord = {};
  for (const key of Object.keys(value).sort()) {
    const child = value[key];
    if (child !== undefined) result[key] = canonicalize(child, seen);
  }
  seen.delete(value);
  return result;
}

function canonicalJson(value: unknown): string {
  return JSON.stringify(canonicalize(value));
}

function canonicalSort<T>(values: readonly T[]): T[] {
  return [...values].sort((left, right) => {
    const a = canonicalJson(left);
    const b = canonicalJson(right);
    return a === b ? 0 : a < b ? -1 : 1;
  });
}

function uniqueCanonical<T>(values: readonly T[]): T[] {
  const result: T[] = [];
  let previous: string | undefined;
  for (const value of canonicalSort(values)) {
    const encoded = canonicalJson(value);
    if (encoded !== previous) result.push(value);
    previous = encoded;
  }
  return result;
}

function sha256(value: unknown): Sha256 {
  return `sha256:${nodeCrypto().createHash("sha256")
    .update(canonicalJson(value), "utf8")
    .digest("hex")}`;
}

function hmacTuple(
  projectionKey: Uint8Array,
  tuple: readonly unknown[],
): OpaqueId {
  return `hmac-sha256:${nodeCrypto().createHmac("sha256", projectionKey)
    .update(canonicalJson(tuple), "utf8")
    .digest("hex")}`;
}

function keyedId(
  projectionKey: Uint8Array,
  domainTag: string,
  value: unknown,
): OpaqueId {
  return hmacTuple(projectionKey, [NAMESPACE, domainTag, value]);
}

function sourceString(value: unknown, code: string): string {
  if (typeof value !== "string" || value.length === 0) throw new Error(code);
  return value.normalize("NFKC");
}

function primitive(value: unknown): Primitive {
  if (value === null || typeof value === "boolean") return value;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") return value.normalize("NFKC");
  throw new Error("DESIGN_TOKEN_ONTOLOGY_CANDIDATE_TOKEN_VALUE_INVALID");
}

function typedPrimitive(value: Primitive): string {
  if (value === null) return "null";
  if (typeof value === "boolean") return `boolean:${String(value)}`;
  if (typeof value === "number") return `number:${JSON.stringify(value)}`;
  return `string:${value.normalize("NFKC")}`;
}

function tokenType(value: unknown): DesignTokenType {
  const normalized =
    typeof value === "string" ? value.normalize("NFKC").toLowerCase() : "";
  if (normalized === "color") return "color";
  if (normalized === "number" || normalized === "float") return "number";
  if (normalized === "string") return "string";
  if (normalized === "boolean" || normalized === "bool") return "boolean";
  return "unknown";
}

function observationId(
  projectionKey: Uint8Array,
  role: "token-value" | "mode-token-value",
  value: Primitive,
): OpaqueId {
  return keyedId(
    projectionKey,
    `opaque-observation:${role}`,
    typedPrimitive(value),
  );
}

function aliasObservationId(
  projectionKey: Uint8Array,
  role: "mode-token-value",
): OpaqueId {
  return keyedId(
    projectionKey,
    `opaque-observation:${role}`,
    "alias:resolved-locally",
  );
}

function makeFactIdentity(
  projectionKey: Uint8Array,
  factType: "variable-collection",
  identity: unknown,
  discriminator: Omit<
    StructuralFact,
    "semanticGroupOpaqueId" | "subjectOpaqueId" | "multiplicity"
  >,
): Pick<StructuralFact, "semanticGroupOpaqueId" | "subjectOpaqueId"> {
  const semanticGroupOpaqueId = keyedId(projectionKey, factType, identity);
  const subjectOpaqueId = hmacTuple(projectionKey, [
    NAMESPACE,
    "fact-subject",
    semanticGroupOpaqueId,
    discriminator,
  ]);
  return { semanticGroupOpaqueId, subjectOpaqueId };
}

function localKey(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

function rawArray(value: unknown): JsonRecord[] {
  if (value === undefined || value === null) return [];
  if (!Array.isArray(value) || value.some((entry) => !isRecord(entry))) {
    throw new Error("DESIGN_TOKEN_ONTOLOGY_CANDIDATE_READBACK_INVALID");
  }
  return value as JsonRecord[];
}

function roleForName(name: string): {
  canonicalRole: CanonicalDesignTokenRole;
  gapCode?: "unmapped-token-role" | "ambiguous-token-role";
} {
  const words = name
    .normalize("NFKC")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .toLocaleLowerCase("en-US")
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean);
  const vocabulary: Array<{
    role: Exclude<CanonicalDesignTokenRole, "unmapped">;
    words: readonly string[];
  }> = [
    { role: "color", words: ["color", "colour", "palette"] },
    {
      role: "typography",
      words: ["typography", "font", "typeface", "lineheight"],
    },
    {
      role: "spacing",
      words: ["space", "spacing", "gap", "padding", "margin"],
    },
    {
      role: "sizing",
      words: ["size", "sizing", "width", "height", "dimension"],
    },
    { role: "radius", words: ["radius", "rounded", "corner"] },
    { role: "border", words: ["border", "stroke", "outline"] },
    { role: "elevation", words: ["elevation", "shadow"] },
    { role: "opacity", words: ["opacity", "alpha"] },
  ];
  const matches = vocabulary
    .filter((entry) => entry.words.some((word) => words.includes(word)))
    .map((entry) => entry.role);
  if (matches.length === 1) return { canonicalRole: matches[0]! };
  if (matches.length > 1) {
    return {
      canonicalRole: "unmapped",
      gapCode: "ambiguous-token-role",
    };
  }
  return {
    canonicalRole: "unmapped",
    gapCode: "unmapped-token-role",
  };
}

function relationTarget(
  fact: StructuralFact,
  role: StructuralFact["relations"][number]["role"],
): OpaqueId | undefined {
  return fact.relations.find((entry) => entry.role === role)
    ?.targetSubjectOpaqueId;
}

function structuralTokenType(fact: StructuralFact): DesignTokenType {
  const code = fact.semanticCodes.find((entry) =>
    entry.startsWith("token-type:"),
  );
  const value = code?.slice("token-type:".length);
  return value === "color" ||
    value === "number" ||
    value === "string" ||
    value === "boolean" ||
    value === "unknown"
    ? value
    : "unknown";
}

function readbackCollections(
  readback: unknown,
  projectionKey: Uint8Array,
  collectionFacts: readonly StructuralFact[],
  selectedCollectionNames?: ReadonlySet<string>,
): RawCollection[] {
  if (!isRecord(readback)) {
    throw new Error("DESIGN_TOKEN_ONTOLOGY_CANDIDATE_READBACK_INVALID");
  }
  const variables = isRecord(readback.variables) ? readback.variables : {};
  const rawCollections = rawArray(variables.collectionSamples).filter(
    (raw) =>
      selectedCollectionNames === undefined ||
      (typeof raw.name === "string" &&
        selectedCollectionNames.has(raw.name.normalize("NFKC").trim())),
  );
  const rawTokens = rawArray(variables.samples);
  const collections: RawCollection[] = rawCollections.map((raw) => {
    const name = sourceString(
      raw.name,
      "DESIGN_TOKEN_ONTOLOGY_CANDIDATE_COLLECTION_NAME_REQUIRED",
    );
    const modes = rawArray(raw.modes).map((modeRaw) => {
      const modeName = sourceString(
        modeRaw.name,
        "DESIGN_TOKEN_ONTOLOGY_CANDIDATE_MODE_NAME_REQUIRED",
      );
      const nameId = keyedId(
        projectionKey,
        "mode-name",
        typedPrimitive(modeName),
      );
      const isDefault =
        modeRaw.isDefault === undefined ? null : modeRaw.isDefault;
      if (isDefault !== null && typeof isDefault !== "boolean") {
        throw new Error("DESIGN_TOKEN_ONTOLOGY_CANDIDATE_MODE_INVALID");
      }
      return {
        raw: modeRaw,
        name: modeName,
        nameId,
        signatureId: keyedId(projectionKey, "mode-structural-signature", [
          nameId,
          isDefault,
        ]),
      };
    });
    return {
      raw,
      name,
      nameId: keyedId(projectionKey, "collection-name", typedPrimitive(name)),
      modes,
      tokens: [],
    };
  });
  const collectionsById = new Map<string, RawCollection[]>();
  const collectionsByName = new Map<string, RawCollection[]>();
  for (const collection of collections) {
    const id = localKey(collection.raw.id);
    if (id) {
      collectionsById.set(id, [...(collectionsById.get(id) ?? []), collection]);
    }
    collectionsByName.set(collection.name, [
      ...(collectionsByName.get(collection.name) ?? []),
      collection,
    ]);
  }
  const collectionForToken = (raw: JsonRecord): RawCollection => {
    const id = localKey(raw.collectionId);
    const name =
      typeof raw.collectionName === "string"
        ? raw.collectionName.normalize("NFKC")
        : undefined;
    const candidates = id
      ? collectionsById.get(id)
      : name
        ? collectionsByName.get(name)
        : undefined;
    if (
      !candidates ||
      candidates.length !== 1 ||
      (name !== undefined && candidates[0]!.name !== name)
    ) {
      throw new Error(
        "DESIGN_TOKEN_ONTOLOGY_CANDIDATE_COLLECTION_BINDING_INVALID",
      );
    }
    return candidates[0]!;
  };
  for (const raw of rawTokens) {
    const collectionId = localKey(raw.collectionId);
    const collectionName =
      typeof raw.collectionName === "string"
        ? raw.collectionName.normalize("NFKC")
        : undefined;
    if (
      selectedCollectionNames !== undefined &&
      !(
        (collectionId !== undefined && collectionsById.has(collectionId)) ||
        (collectionName !== undefined && collectionsByName.has(collectionName))
      )
    ) {
      continue;
    }
    const collection = collectionForToken(raw);
    const name = sourceString(
      raw.name,
      "DESIGN_TOKEN_ONTOLOGY_CANDIDATE_TOKEN_NAME_REQUIRED",
    );
    const nameId = keyedId(projectionKey, "token-name", typedPrimitive(name));
    const type = tokenType(raw.tokenType ?? raw.type);
    const baseValue = raw.value === undefined ? null : primitive(raw.value);
    const baseObservationId = observationId(
      projectionKey,
      "token-value",
      baseValue,
    );
    const modePairs = rawArray(raw.valuesByMode).map((modeRaw) => {
      const modeId = localKey(modeRaw.modeId);
      const modeName =
        typeof (modeRaw.modeName ?? modeRaw.name) === "string"
          ? String(modeRaw.modeName ?? modeRaw.name).normalize("NFKC")
          : undefined;
      const candidates = collection.modes.filter(
        (mode) =>
          (modeId !== undefined && localKey(mode.raw.modeId) === modeId) ||
          (modeId === undefined &&
            modeName !== undefined &&
            mode.name === modeName),
      );
      if (
        candidates.length !== 1 ||
        (modeName !== undefined && candidates[0]!.name !== modeName)
      ) {
        throw new Error("DESIGN_TOKEN_ONTOLOGY_CANDIDATE_MODE_BINDING_INVALID");
      }
      const valueId =
        modeRaw.value !== undefined
          ? observationId(
              projectionKey,
              "mode-token-value",
              primitive(modeRaw.value),
            )
          : aliasObservationId(projectionKey, "mode-token-value");
      return { mode: candidates[0]!, observationId: valueId };
    });
    const modePairIds = modePairs.map((entry) =>
      keyedId(projectionKey, "token-mode-value-pair", [
        entry.mode.nameId,
        entry.observationId,
      ]),
    );
    const token: RawToken = {
      raw,
      name,
      nameId,
      tokenType: type,
      tokenTypeCode: `token-type:${type}`,
      baseObservationId,
      modePairs,
      signatureId: keyedId(projectionKey, "token-structural-signature", [
        nameId,
        `token-type:${type}`,
        baseObservationId,
        uniqueCanonical(modePairIds),
      ]),
    };
    collection.tokens.push(token);
  }
  for (const collection of collections) {
    const confidence =
      collection.raw.confidence === undefined
        ? undefined
        : collection.raw.confidence;
    if (
      confidence !== undefined &&
      (typeof confidence !== "number" ||
        !Number.isFinite(confidence) ||
        confidence < 0 ||
        confidence > 1)
    ) {
      throw new Error(
        "DESIGN_TOKEN_ONTOLOGY_CANDIDATE_COLLECTION_CONFIDENCE_INVALID",
      );
    }
    const discriminator = {
      factType: "variable-collection" as const,
      semanticCodes: [],
      observations: [],
      relations: [],
      metrics: canonicalSort([
        { role: "token-count" as const, value: collection.tokens.length },
        ...(confidence === undefined
          ? []
          : [{ role: "confidence" as const, value: confidence }]),
      ]),
      flags: [],
    };
    const identity = makeFactIdentity(
      projectionKey,
      "variable-collection",
      [
        collection.nameId,
        uniqueCanonical(collection.modes.map((mode) => mode.nameId)),
        uniqueCanonical(collection.tokens.map((token) => token.signatureId)),
      ],
      discriminator,
    );
    collection.semanticGroupOpaqueId = identity.semanticGroupOpaqueId;
    collection.subjectOpaqueId = identity.subjectOpaqueId;
    if (
      !collectionFacts.some(
        (fact) =>
          fact.semanticGroupOpaqueId === identity.semanticGroupOpaqueId &&
          fact.subjectOpaqueId === identity.subjectOpaqueId,
      )
    ) {
      throw new Error(
        "DESIGN_TOKEN_ONTOLOGY_CANDIDATE_COLLECTION_PROJECTION_JOIN_FAILED",
      );
    }
  }
  return collections;
}

export interface DeriveSelectedDesignTokenCollectionOpaqueIdsV2Input {
  readback: unknown;
  selection: SourceStructureSelectionV1;
  projectionKey: Uint8Array;
  structuralFacts: readonly StructuralFact[];
}

function adoptedVariableCollectionNames(
  selection: SourceStructureSelectionV1,
): string[] {
  const selectedNames = selection.members.flatMap((member) => {
    const name = member.structuralPath[2];
    return member.structuralPath.length === 3 &&
      member.structuralPath[0] === "variables" &&
      member.structuralPath[1] === "collections" &&
      typeof name === "string"
      ? [name]
      : [];
  });
  if (
    selectedNames.length === 0 ||
    new Set(selectedNames).size !== selectedNames.length
  ) {
    throw new Error(
      "DESIGN_TOKEN_ONTOLOGY_CANDIDATE_COLLECTION_SELECTION_EMPTY",
    );
  }
  return selectedNames;
}

export async function deriveSelectedDesignTokenCollectionOpaqueIdsV2(
  input: DeriveSelectedDesignTokenCollectionOpaqueIdsV2Input,
): Promise<OpaqueId[]> {
  const selection = SourceStructureSelectionV1Schema.safeParse(input.selection);
  if (
    !selection.success ||
    !resolveOpenPencilSourceStructureSelection(
      input.readback as Parameters<
        typeof resolveOpenPencilSourceStructureSelection
      >[0],
      selection.data,
    ).passed
  ) {
    throw new Error(
      "DESIGN_TOKEN_ONTOLOGY_CANDIDATE_COLLECTION_SELECTION_INVALID",
    );
  }
  const selectedNames = adoptedVariableCollectionNames(selection.data);
  const collectionFacts = input.structuralFacts.filter(
    (fact) => fact.factType === "variable-collection",
  );
  const collections = readbackCollections(
    input.readback,
    input.projectionKey,
    collectionFacts,
    new Set(selectedNames),
  );
  if (
    collections.length !== selectedNames.length ||
    new Set(collections.map((collection) => collection.name)).size !==
      selectedNames.length
  ) {
    throw new Error(
      "DESIGN_TOKEN_ONTOLOGY_CANDIDATE_COLLECTION_SELECTION_MISMATCH",
    );
  }
  const ids = collections.map((collection) => {
    if (!collection.semanticGroupOpaqueId) {
      throw new Error(
        "DESIGN_TOKEN_ONTOLOGY_CANDIDATE_COLLECTION_PROJECTION_JOIN_FAILED",
      );
    }
    return collection.semanticGroupOpaqueId;
  });
  return [...new Set(ids)].sort();
}

export async function buildDesignTokenOntologyCandidate(
  input: DesignTokenOntologyCandidateInputV2,
): Promise<DesignTokenOntologyCandidateV2> {
  const preflight = await preflightOpenPencilDesignTokenBoundaryV2(input);
  if (
    !Array.isArray(input.allowedCollectionOpaqueIds) ||
    input.allowedCollectionOpaqueIds.length === 0 ||
    input.allowedCollectionOpaqueIds.some(
      (value) => typeof value !== "string" || !OPAQUE_PATTERN.test(value),
    ) ||
    new Set(input.allowedCollectionOpaqueIds).size !==
      input.allowedCollectionOpaqueIds.length
  ) {
    throw new Error(
      "DESIGN_TOKEN_ONTOLOGY_CANDIDATE_COLLECTION_SELECTION_INVALID",
    );
  }
  const reuseReceipt = isRecord(input.libraryReuseAdmissionReceipt)
    ? input.libraryReuseAdmissionReceipt
    : {};
  const selection = SourceStructureSelectionV1Schema.safeParse(
    reuseReceipt.selection,
  );
  if (!selection.success) {
    throw new Error(
      "DESIGN_TOKEN_ONTOLOGY_CANDIDATE_COLLECTION_SELECTION_INVALID",
    );
  }
  const selectedCollectionNames = new Set(
    adoptedVariableCollectionNames(selection.data),
  );
  const facts = getPreflightOpenPencilOntologyFactsV2(input, preflight);
  const collectionFacts = facts.filter(
    (fact) => fact.factType === "variable-collection",
  );
  const selectedFacts = input.allowedCollectionOpaqueIds.map((opaqueId) => {
    const matches = collectionFacts.filter(
      (fact) => fact.semanticGroupOpaqueId === opaqueId,
    );
    if (matches.length !== 1) {
      throw new Error(
        "DESIGN_TOKEN_ONTOLOGY_CANDIDATE_COLLECTION_SELECTION_MISMATCH",
      );
    }
    return matches[0]!;
  });

  const verified = openVerifiedOpenPencilDesignTokenReadbackV2(
    input,
    preflight,
  );
  const readback = verified.readback;
  const projectionKey = verified.projectionKey;
  const authorizedCollectionOpaqueIds =
    await deriveSelectedDesignTokenCollectionOpaqueIdsV2({
      readback,
      selection: selection.data,
      projectionKey,
      structuralFacts: facts,
    });
  if (
    canonicalJson(authorizedCollectionOpaqueIds) !==
    canonicalJson([...input.allowedCollectionOpaqueIds].sort())
  ) {
    throw new Error(
      "DESIGN_TOKEN_ONTOLOGY_CANDIDATE_COLLECTION_SELECTION_MISMATCH",
    );
  }

  const selectedSubjects = new Set(
    selectedFacts.map((fact) => fact.subjectOpaqueId),
  );
  const joinedCollections = readbackCollections(
    readback,
    projectionKey,
    collectionFacts,
    selectedCollectionNames,
  ).filter(
    (collection) =>
      collection.subjectOpaqueId !== undefined &&
      selectedSubjects.has(collection.subjectOpaqueId),
  );
  const collections = selectedFacts.map((selectedFact) => {
    const matches = joinedCollections.filter(
      (collection) =>
        collection.subjectOpaqueId === selectedFact.subjectOpaqueId &&
        collection.semanticGroupOpaqueId === selectedFact.semanticGroupOpaqueId,
    );
    if (matches.length === 0) {
      throw new Error(
        "DESIGN_TOKEN_ONTOLOGY_CANDIDATE_COLLECTION_READBACK_SELECTION_MISMATCH",
      );
    }
    return matches[0]!;
  });

  const modeFacts = facts.filter((fact) => fact.factType === "collection-mode");
  const tokenFacts = facts.filter((fact) => fact.factType === "design-token");
  const tokenModeFacts = facts.filter(
    (fact) => fact.factType === "token-mode-observation",
  );
  const sourceRefs = verified.sourceRefs;
  const outputCollections: DesignTokenOntologyCandidateV2["collections"] = [];
  const tokenRoles: DesignTokenOntologyCandidateV2["tokenRoles"] = [];
  const gaps: DesignTokenOntologyCandidateV2["gaps"] = [];

  for (const collection of collections) {
    const collectionSubject = collection.subjectOpaqueId!;
    const collectionSemanticRef = collection.semanticGroupOpaqueId!;
    const relatedModeFacts = modeFacts.filter(
      (fact) =>
        relationTarget(fact, "belongs-to-collection") === collectionSubject,
    );
    const modeSemanticRefs = collection.modes.map((mode) => {
      const expectedGroup = keyedId(projectionKey, "collection-mode", [
        collectionSubject,
        mode.nameId,
        mode.signatureId,
      ]);
      const matches = relatedModeFacts.filter(
        (fact) => fact.semanticGroupOpaqueId === expectedGroup,
      );
      if (matches.length !== 1) {
        throw new Error(
          "DESIGN_TOKEN_ONTOLOGY_CANDIDATE_MODE_PROJECTION_JOIN_FAILED",
        );
      }
      return matches[0]!.semanticGroupOpaqueId;
    });
    const selectedCollectionFact = selectedFacts.find(
      (fact) => fact.subjectOpaqueId === collectionSubject,
    )!;
    const tokenCount = selectedCollectionFact.metrics.find(
      (entry) => entry.role === "token-count",
    )?.value;
    if (!Number.isInteger(tokenCount) || (tokenCount as number) < 0) {
      throw new Error("DESIGN_TOKEN_ONTOLOGY_CANDIDATE_TOKEN_COUNT_INVALID");
    }
    outputCollections.push({
      sourceSemanticRef: collectionSemanticRef,
      modeSemanticRefs: uniqueCanonical(modeSemanticRefs),
      tokenCount: tokenCount as number,
    });

    for (const token of collection.tokens) {
      const expectedTokenGroup = keyedId(projectionKey, "design-token", [
        collectionSubject,
        token.nameId,
        token.tokenTypeCode,
        token.baseObservationId,
        uniqueCanonical(
          token.modePairs.map((entry) =>
            keyedId(projectionKey, "token-mode-value-pair", [
              entry.mode.nameId,
              entry.observationId,
            ]),
          ),
        ),
      ]);
      const matches = tokenFacts.filter(
        (fact) =>
          fact.semanticGroupOpaqueId === expectedTokenGroup &&
          relationTarget(fact, "belongs-to-collection") === collectionSubject,
      );
      if (matches.length !== 1) {
        throw new Error(
          "DESIGN_TOKEN_ONTOLOGY_CANDIDATE_TOKEN_PROJECTION_JOIN_FAILED",
        );
      }
      const tokenFact = matches[0]!;
      const verifiedTokenType = structuralTokenType(tokenFact);
      if (verifiedTokenType !== token.tokenType) {
        throw new Error("DESIGN_TOKEN_ONTOLOGY_CANDIDATE_TOKEN_TYPE_MISMATCH");
      }
      const role = roleForName(token.name);
      const sourceSemanticRef = tokenFact.semanticGroupOpaqueId;
      tokenRoles.push({
        sourceSemanticRef,
        tokenType: verifiedTokenType,
        canonicalRole: role.canonicalRole,
        semanticSignature: sha256({
          canonicalRole: role.canonicalRole,
          sourceRefs,
          sourceSemanticRef,
          tokenType: verifiedTokenType,
        }),
      });
      if (role.gapCode) {
        gaps.push({ code: role.gapCode, sourceSemanticRef });
      }
      const expectedModeSubjects = new Set(
        token.modePairs.map((entry) => {
          const expectedGroup = keyedId(projectionKey, "collection-mode", [
            collectionSubject,
            entry.mode.nameId,
            entry.mode.signatureId,
          ]);
          return relatedModeFacts.find(
            (fact) => fact.semanticGroupOpaqueId === expectedGroup,
          )?.subjectOpaqueId;
        }),
      );
      const observedModeSubjects = new Set(
        tokenModeFacts
          .filter((fact) =>
            fact.relations.some(
              (relation) =>
                relation.role === "observes-token" &&
                relation.targetSubjectOpaqueId === tokenFact.subjectOpaqueId,
            ),
          )
          .map((fact) => relationTarget(fact, "belongs-to-mode"))
          .filter((value): value is OpaqueId => value !== undefined),
      );
      if (
        [...expectedModeSubjects].some(
          (subject) =>
            subject === undefined || !observedModeSubjects.has(subject),
        )
      ) {
        gaps.push({ code: "mode-inconsistency", sourceSemanticRef });
      }
    }
  }

  return {
    schemaVersion: "cogo.design-token-ontology-candidate.v2",
    collections: canonicalSort(outputCollections),
    tokenRoles: uniqueCanonical(tokenRoles),
    gaps: uniqueCanonical(gaps),
    sourceRefs,
    authority: {
      candidateOnly: true,
      generationEligible: false,
    },
  };
}

export async function verifyDesignTokenOntologyCandidate(
  input: DesignTokenOntologyCandidateInputV2,
  candidate: unknown,
): Promise<{ valid: boolean; issues: string[] }> {
  try {
    const expected = await buildDesignTokenOntologyCandidate(input);
    if (canonicalJson(expected) !== canonicalJson(candidate)) {
      return {
        valid: false,
        issues: ["DESIGN_TOKEN_ONTOLOGY_CANDIDATE_RECOMPUTATION_MISMATCH"],
      };
    }
    return { valid: true, issues: [] };
  } catch (error) {
    return {
      valid: false,
      issues: [
        error instanceof Error
          ? error.message
          : "DESIGN_TOKEN_ONTOLOGY_CANDIDATE_VERIFICATION_FAILED",
      ],
    };
  }
}
